import { useLang } from '../i18n/LangContext'
import {
  User, PenTool, Settings, Cpu, BarChart3, Database,
  ChevronRight, ArrowRight, ShieldCheck, FileText, TrendingUp, RefreshCw,
} from 'lucide-react'

interface BiText {
  zh: string
  en: string
}

interface Stage {
  num: number
  icon: typeof User
  color: string
  title: BiText
  desc: BiText
}

const stages: Stage[] = [
  {
    num: 1,
    icon: User,
    color: '#6c8cff',
    title: { zh: '专家提交任务', en: 'Expert Submits Task' },
    desc: { zh: '教师/专家在贡献页面提交职业任务', en: 'Teachers/experts submit professional tasks' },
  },
  {
    num: 2,
    icon: PenTool,
    color: '#4ade80',
    title: { zh: '专家提供参考解法', en: 'Expert Provides Reference' },
    desc: {
      zh: '给出推荐方案、关键决策点和常见错误',
      en: 'Provides reference solution, key decisions, common mistakes',
    },
  },
  {
    num: 3,
    icon: Settings,
    color: '#fbbf24',
    title: { zh: '平台结构化', en: 'Platform Structures' },
    desc: {
      zh: '任务变为Task+Rubric，自动生成评分规则',
      en: 'Task becomes Task+Rubric with auto-generated scoring rules',
    },
  },
  {
    num: 4,
    icon: Cpu,
    color: '#f87171',
    title: { zh: '多模型运行', en: 'Multi-Model Execution' },
    desc: {
      zh: 'GPT-4o/Claude/DeepSeek等在同一任务上运行',
      en: 'Multiple AI models run on the same task',
    },
  },
  {
    num: 5,
    icon: BarChart3,
    color: '#60a5fa',
    title: { zh: '轨迹记录与评分', en: 'Trace Recording & Scoring' },
    desc: {
      zh: '记录完整决策轨迹，多维度评分',
      en: 'Records full decision traces, multi-dimensional scoring',
    },
  },
  {
    num: 6,
    icon: Database,
    color: '#a78bfa',
    title: { zh: '形成数据集', en: 'Dataset Formation' },
    desc: {
      zh: '评测集+训练集，反馈给专家和开发者',
      en: 'Benchmark+training sets, feedback to experts and developers',
    },
  },
]

interface DataUsageItem {
  label: BiText
  value: BiText
}

interface DataUsageColumn {
  icon: typeof ShieldCheck
  accentColor: string
  title: BiText
  items: DataUsageItem[]
}

const dataUsageColumns: DataUsageColumn[] = [
  {
    icon: BarChart3,
    accentColor: '#60a5fa',
    title: { zh: '评测集', en: 'Benchmark Dataset' },
    items: [
      {
        label: { zh: '用途', en: 'Purpose' },
        value: { zh: '模型能力评估、排行榜生成', en: 'Model capability evaluation, leaderboard generation' },
      },
      {
        label: { zh: '数据类型', en: 'Data Type' },
        value: { zh: 'Task + Rubric + Trace + Score', en: 'Task + Rubric + Trace + Score' },
      },
      {
        label: { zh: '是否脱敏', en: 'Anonymized' },
        value: { zh: '是（去除Agent身份信息）', en: 'Yes (Agent identity removed)' },
      },
      {
        label: { zh: '授权', en: 'License' },
        value: { zh: 'CC-BY-4.0（默认）', en: 'CC-BY-4.0 (default)' },
      },
      {
        label: { zh: '质量要求', en: 'Quality Requirement' },
        value: { zh: '专家审核通过，质量等级B+以上', en: 'Expert-approved, quality grade B+ or above' },
      },
    ],
  },
  {
    icon: Database,
    accentColor: '#a78bfa',
    title: { zh: '训练集', en: 'Training Dataset' },
    items: [
      {
        label: { zh: '用途', en: 'Purpose' },
        value: { zh: '模型微调、强化学习', en: 'Model fine-tuning, reinforcement learning' },
      },
      {
        label: { zh: '数据类型', en: 'Data Type' },
        value: { zh: '参考解法 + 高分轨迹 + 评分反馈', en: 'Reference + high-score traces + score feedback' },
      },
      {
        label: { zh: '是否脱敏', en: 'Anonymized' },
        value: { zh: '是（去除个人信息和敏感数据）', en: 'Yes (personal info & sensitive data removed)' },
      },
      {
        label: { zh: '授权', en: 'License' },
        value: { zh: '需专家单独授权（allow_training_use=true）', en: 'Requires separate expert authorization (allow_training_use=true)' },
      },
      {
        label: { zh: '质量要求', en: 'Quality Requirement' },
        value: { zh: '质量等级A以上，参考解法完整', en: 'Quality grade A or above, complete reference solution' },
      },
    ],
  },
]

interface FeedbackItem {
  icon: typeof TrendingUp
  accentColor: string
  from: BiText
  to: BiText
  desc: BiText
}

const feedbackItems: FeedbackItem[] = [
  {
    icon: BarChart3,
    accentColor: '#60a5fa',
    from: { zh: '评测结果', en: 'Evaluation Results' },
    to: { zh: '专家', en: 'Experts' },
    desc: {
      zh: '您的任务被X个模型评测，通过率Y%',
      en: 'Your task was evaluated by X models, pass rate Y%',
    },
  },
  {
    icon: TrendingUp,
    accentColor: '#f87171',
    from: { zh: '失败模式', en: 'Failure Modes' },
    to: { zh: '模型开发者', en: 'Model Developers' },
    desc: {
      zh: '模型在Z类任务上表现不佳，常见错误...',
      en: 'Model underperforms on Z-type tasks, common errors...',
    },
  },
  {
    icon: Database,
    accentColor: '#a78bfa',
    from: { zh: '高分轨迹', en: 'High-Score Traces' },
    to: { zh: '训练集', en: 'Training Set' },
    desc: {
      zh: '质量等级A以上的轨迹可用于训练',
      en: 'Traces with quality grade A or above can be used for training',
    },
  },
  {
    icon: ShieldCheck,
    accentColor: '#4ade80',
    from: { zh: '参考解法', en: 'Reference Solutions' },
    to: { zh: '校准', en: 'Calibration' },
    desc: {
      zh: '专家参考答案用于评分校准和标准对齐',
      en: 'Expert reference answers used for scoring calibration & standard alignment',
    },
  },
]

export default function DataPipelinePage() {
  const { lang } = useLang()

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* ============ 1. Header ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-accent-glow)' }}
          >
            <RefreshCw size={22} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {lang === 'zh' ? '数据闭环' : 'Data Pipeline'}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              {lang === 'zh'
                ? '从专家贡献到模型训练的完整数据流'
                : 'The complete data flow from expert contribution to model training'}
            </p>
          </div>
        </div>
      </section>

      {/* ============ 2. Flow Diagram ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-5">
          <ArrowRight size={18} style={{ color: 'var(--color-accent)' }} />
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {lang === 'zh' ? '数据闭环流程' : 'Data Loop Flow'}
          </h2>
        </div>

        <div
          className="rounded-xl border p-5 sm:p-6"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between gap-3">
            {stages.map((stage, idx) => {
              const Icon = stage.icon
              return (
                <div key={stage.num} className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-2 flex-1">
                  {/* Stage Card */}
                  <div
                    className="rounded-xl p-4 flex-1 h-full"
                    style={{
                      backgroundColor: 'var(--color-bg-card)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {/* Numbered circle */}
                      <div
                        className="shrink-0 flex items-center justify-center text-sm font-bold"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: stage.color,
                          color: '#fff',
                        }}
                      >
                        {stage.num}
                      </div>
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                      >
                        <Icon size={18} style={{ color: stage.color }} />
                      </div>
                    </div>
                    <h3
                      className="text-sm font-semibold mb-1.5 leading-snug"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {stage.title[lang]}
                    </h3>
                    <p
                      className="text-xs leading-relaxed"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {stage.desc[lang]}
                    </p>
                  </div>

                  {/* Arrow between stages */}
                  {idx < stages.length - 1 && (
                    <div
                      className="flex items-center justify-center lg:shrink-0"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <ChevronRight
                        size={24}
                        className="rotate-90 lg:rotate-0"
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ 3. Data Usage Section ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-5">
          <FileText size={18} style={{ color: 'var(--color-accent)' }} />
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {lang === 'zh' ? '数据使用说明' : 'Data Usage'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {dataUsageColumns.map((col) => {
            const Icon = col.icon
            return (
              <div
                key={col.title.en}
                className="rounded-xl border p-6"
                style={{
                  backgroundColor: 'var(--color-bg-card)',
                  borderColor: 'var(--color-border)',
                }}
              >
                {/* Column Header */}
                <div className="flex items-center gap-3 mb-5 pb-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${col.accentColor}1f` }}
                  >
                    <Icon size={20} style={{ color: col.accentColor }} />
                  </div>
                  <h3
                    className="text-base font-semibold"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {col.title[lang]}
                  </h3>
                </div>

                {/* Items */}
                <ul className="space-y-4">
                  {col.items.map((item) => (
                    <li key={item.label.en} className="flex flex-col gap-1">
                      <span
                        className="text-[11px] font-semibold uppercase tracking-wide"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {item.label[lang]}
                      </span>
                      <span
                        className="text-sm leading-relaxed"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {item.value[lang]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </section>

      {/* ============ 4. Feedback Loop Section ============ */}
      <section>
        <div className="flex items-center gap-2 mb-5">
          <RefreshCw size={18} style={{ color: 'var(--color-accent)' }} />
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {lang === 'zh' ? '数据回流闭环' : 'Feedback Loop'}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {feedbackItems.map((item) => {
            const Icon = item.icon
            return (
              <div
                key={`${item.from.en}-${item.to.en}`}
                className="rounded-xl border p-5"
                style={{
                  backgroundColor: 'var(--color-bg-card)',
                  borderColor: 'var(--color-border)',
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${item.accentColor}1f` }}
                  >
                    <Icon size={18} style={{ color: item.accentColor }} />
                  </div>
                  {/* from → to */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-sm font-semibold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {item.from[lang]}
                    </span>
                    <ArrowRight size={14} style={{ color: 'var(--color-text-muted)' }} />
                    <span
                      className="text-sm font-semibold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {item.to[lang]}
                    </span>
                  </div>
                </div>
                <p
                  className="text-xs leading-relaxed pl-12"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.desc[lang]}
                </p>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
